import { useState } from "react";
import { DataDefinition } from "../../../rule-engine-core/Common/Data/DataDefinition";

import JSONInput from "react-json-editor-ajrm";
import { locale } from "../../../rule-engine-core/Common/JsonInputLocale";

import { Box, Button } from "@mui/material";
import { EditModal } from "./EditModal";
import { EditIcon } from "../../Icon/EditIcon";

import { DataStructureDifferences } from "../../../rule-engine-core/Common/Data/ExecutionData/DataStructureDifferences";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { ObjectDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";
import { CompareChangesLayer } from "./CompareChangesLayer";
import { OpenAIChatLink } from "../../OpenAI/OpenAIChatLink";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";

export const SchemaUIComponent = (props: { definition: DataDefinition }) => {
    const [openSchemaModal, setOpenSchemaModal] = useState<boolean>(false);
    const [dataChanges, setDataChanges] = useState<DataStructureDifferences[]>([]);
    const [updatedDef, setUpdatedDef] = useState<DataDefinition | undefined>();
    const [applyError, setApplyError] = useState<string | undefined>();

    const onToggleSchemaModal = () => {
        setOpenSchemaModal(!openSchemaModal);
    };

    const onApply = () => {
        if (!updatedDef) {
            onCancel();
            return;
        }

        props.definition.fields = updatedDef.fields;

        StateManager.changed(props.definition);

        setDataChanges([]);
        setUpdatedDef(undefined);
        setOpenSchemaModal(false);
    };

    const onCancel = () => {
        setDataChanges([]);
        setUpdatedDef(undefined);
        setOpenSchemaModal(false);
    };

    return (
        <Box
            display={"flex"}
            flexDirection={"column"}
            width={"100%"}
        >
            <Box
                display={"flex"}
                flexDirection={"row"}
                marginBottom={"15px"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width={"100%"}
            >
                <Button
                    variant="contained"
                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                    startIcon={<EditIcon />}
                    onClick={onToggleSchemaModal}
                >
                    Edit JSON Schema
                </Button>
                {props.definition.fields.length === 0 && (
                    <OpenAIChatLink
                        linkName={"Generate JSON Schema"}
                        title={"Generate JSON Schema"}
                        needsClean={(t: string) => {
                            try {
                                JSON.parse(t);
                            } catch {
                                return true;
                            }
                            return false;
                        }}
                        onSelectAnswer={(v) => {
                            try {
                                let json = JSON.parse(v);

                                let odef = ObjectDataDefinition.fromJSONSchema(json) as
                                    | ObjectDataDefinition
                                    | undefined;

                                if (!odef) {
                                    throw new Error("invalid schema");
                                }

                                let changes = props.definition.detectDataStructureChanges(odef.fields);

                                if (changes.length > 0) {
                                    let tempDef = new DataDefinition();
                                    tempDef.fields = odef.fields;
                                    setUpdatedDef(tempDef);
                                    setDataChanges([...changes]);
                                } else {
                                    setOpenSchemaModal(false);
                                }
                            } catch (error) {
                                setApplyError(error.message);
                            }
                        }}
                        pretext={"Generate JSON Schema for"}
                    ></OpenAIChatLink>
                )}

                {props.definition.fields.length !== 0 && (
                    <OpenAIChatLink
                        linkName={"Modify JSON Schema"}
                        title={"Modify JSON Schema"}
                        needsClean={(t: string) => {
                            try {
                                JSON.parse(t);
                            } catch {
                                return true;
                            }
                            return false;
                        }}
                        onSelectAnswer={(v) => {
                            try {
                                let json = JSON.parse(v);

                                let odef = ObjectDataDefinition.fromJSONSchema(json) as
                                    | ObjectDataDefinition
                                    | undefined;

                                if (!odef) {
                                    throw new Error("invalid schema");
                                }

                                let changes = props.definition.detectDataStructureChanges(odef.fields);

                                if (changes.length > 0) {
                                    let tempDef = new DataDefinition();
                                    tempDef.fields = odef.fields;
                                    setUpdatedDef(tempDef);
                                    setDataChanges([...changes]);
                                } else {
                                    setOpenSchemaModal(false);
                                }
                            } catch (error) {
                                setApplyError(error.message);
                            }
                        }}
                        pretext={"Modify this json schema by "}
                        posttext={"Schema: " + props.definition.toJSONSchema()}
                    ></OpenAIChatLink>
                )}
            </Box>
            <JSONInput
                placeholder={JSON.parse(props.definition.toJSONSchema())}
                locale={locale}
                theme={"light_mitsuketa_tribute"}
                style={{
                    outerBox: {
                        width: "100%",
                    },
                    container: {
                        width: "100%",
                    },
                }}
                viewOnly
            />
            <EditModal
                open={openSchemaModal}
                value={props.definition.toJSONSchema()}
                title={"Edit JSON Schema"}
                error={applyError}
                onEdit={(value: object) => {
                    try {
                        let json = value;

                        let odef = ObjectDataDefinition.fromJSONSchema(json) as ObjectDataDefinition | undefined;

                        if (!odef) {
                            throw new Error("invalid schema");
                        }

                        let changes = props.definition.detectDataStructureChanges(odef.fields);

                        if (changes.length > 0) {
                            let tempDef = new DataDefinition();
                            tempDef.fields = odef.fields;
                            setUpdatedDef(tempDef);
                            setDataChanges([...changes]);
                        } else {
                            setOpenSchemaModal(false);
                        }
                    } catch (error) {
                        setApplyError(error.message);
                    }
                }}
                onClose={() => setOpenSchemaModal(false)}
            />
            {dataChanges.length > 0 && (
                <CompareChangesLayer
                    title={"Schema Changes Compare"}
                    oldValue={props.definition.toStructureString(0)}
                    newValue={updatedDef!.toStructureString(0)}
                    onApply={onApply}
                    onCancel={onCancel}
                />
            )}
        </Box>
    );
};
